import { ExportOutlined, FileExcelOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  message,
  Row,
  Space,
  Tag,
  Tooltip,
} from "antd";
import { getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ListTable from "common/ListTable";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import dayjs from "dayjs";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { cglFormatDate } from "utils/functions";

const ProductionPlanningList = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [date, setDate] = useState(dayjs().format("MMM-YYYY"));
  const [fetchError, setFetchError] = useState(null);
  const lastFilterRef = useRef({});

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllProductionPlans = useCallback(
    async (filterValues) => {
      setLoading(true);
      setFetchError(null);
      lastFilterRef.current = filterValues;

      const defaultStartDate = moment().startOf("month").format("YYYY-MM-DD");
      const defaultEndDate = moment().endOf("month").format("YYYY-MM-DD");

      const query = `/api/productionPlanning/plan_initiate/custom-plan-list`;
      const bodyData = {
        startDate: filterValues?.startDate || defaultStartDate,
        endDate: filterValues?.endDate || defaultEndDate,
        page: currentPage,
        ...filterValues,
      };

      try {
        const response = await getData(query, false, bodyData);
        if (response && response?.data?.code === 200) {
          const data = response?.data?.data || [];

          const formattedData = data.filter((item) => item?.id);

          setTableData(formattedData);
          setMetaData({
            total: data.length,
            current: currentPage,
            defaultPageSize: 20,
            summary: data.length,
          });
        } else {
          message.error("Error getting production planning data");
          setFetchError("Error getting production planning data");
        }
      } catch (error) {
        message.error("Error fetching production planning data");
        setFetchError("Error fetching production planning data");
      } finally {
        setLoading(false);
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    localStorage.removeItem("production_planning_filter_value");
    getAllProductionPlans({});
  }, [refresh, getAllProductionPlans]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const gotoAddRoute = () => {
    navigate("/production-planning/plan-entry");
  };

  const gotoUpdateRoute = (record) => {
    navigate(`/production-planning/plan-update/${record.id}`);
  };

  const tableColumns = [
    {
      title: "Line",
      dataIndex: "resourceName",
      key: "resourceName",
      width: "10%",
    },
    {
      title: "Inquiry / Style",
      dataIndex: "title",
      key: "title",
      width: "12%",
    },
    {
      title: "Plan Qty",
      dataIndex: "planned_qty",
      key: "planned_qty",
      width: "10%",
      render: (value) => value?.toLocaleString() || 0,
    },
    {
      title: "Start Date",
      dataIndex: "start",
      key: "start",
      width: "10%",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "End Date",
      dataIndex: "end",
      key: "end",
      width: "10%",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Learning Curve",
      dataIndex: "is_apply_learning_curve",
      key: "is_apply_learning_curve",
      width: "10%",
      render: (value) => (
        <Tag color={value === "1" ? "green" : "red"}>
          {value === "1" ? "Applied" : "Not Applied"}
        </Tag>
      ),
    },

    {
      title: "Action",
      key: "action",
      width: "13%",
      render: (_, record) => {
        const isDisabled = moment().isAfter(moment(record.start), "day");
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            {isDisabled ? (
              <Tooltip title="Cannot edit plan after start date">
                <Button
                  size="small"
                  onClick={() => gotoUpdateRoute(record)}
                  icon={<ExportOutlined />}
                  disabled={isDisabled}
                  title={
                    isDisabled
                      ? "Cannot edit plan after start date"
                      : "Edit plan"
                  }
                >
                  Edit
                </Button>
              </Tooltip>
            ) : (
              <Button
                size="small"
                onClick={() => gotoUpdateRoute(record)}
                icon={<ExportOutlined />}
                disabled={isDisabled}
                title={
                  isDisabled ? "Cannot edit plan after start date" : "Edit plan"
                }
              >
                Edit
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <Space>
              <Button
                type="default"
                icon={<FileExcelOutlined />}
                style={{ borderRadius: "5px" }}
                onClick={() => navigate("/production-planning/planning-report")}
              >
                Planning Report
              </Button>
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={gotoAddRoute}
                addButtonText="Add New Plan"
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            </Space>
          }
          title="Production Planning List"
        />
      }
      content={
        <>
          <Form layout="vertical" style={{ marginLeft: 20 }}>
            <Row gutter={24}>
              <Col span={5}>
                <Form.Item label="Timeline">
                  <DatePicker
                    placeholder={"Select month"}
                    style={{
                      width: "100%",
                    }}
                    format="MMM-YYYY"
                    picker="month"
                    allowClear={false}
                    value={date ? dayjs(date, "MMM-YYYY") : dayjs()}
                    onChange={(_date, dateString) => {
                      if (dateString) {
                        setDate(dateString);
                        // Trigger filter update
                        const startDate = dayjs(dateString, "MMM-YYYY")
                          .startOf("month")
                          .format("YYYY-MM-DD");
                        const endDate = dayjs(dateString, "MMM-YYYY")
                          .endOf("month")
                          .format("YYYY-MM-DD");
                        getAllProductionPlans({ startDate, endDate });
                      } else {
                        const currentMonth = dayjs().format("MMM-YYYY");
                        setDate(currentMonth);
                        const startDate = dayjs(currentMonth, "MMM-YYYY")
                          .startOf("month")
                          .format("YYYY-MM-DD");
                        const endDate = dayjs(currentMonth, "MMM-YYYY")
                          .endOf("month")
                          .format("YYYY-MM-DD");
                        getAllProductionPlans({ startDate, endDate });
                      }
                    }}
                    size="middle"
                  />
                </Form.Item>
              </Col>

              <Col
                span={4}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    marginTop: "10px",
                  }}
                >
                  <Button
                    type="primary"
                    style={{
                      borderRadius: "5px",
                      marginRight: "5px",
                    }}
                    onClick={() => {
                      const startDate = dayjs(date, "MMM-YYYY")
                        .startOf("month")
                        .format("YYYY-MM-DD");
                      const endDate = dayjs(date, "MMM-YYYY")
                        .endOf("month")
                        .format("YYYY-MM-DD");
                      getAllProductionPlans({ startDate, endDate });

                      // Save filter to localStorage
                      localStorage.setItem(
                        "production_planning_filter_value",
                        JSON.stringify({ startDate, endDate, timeline: date }),
                      );
                    }}
                  >
                    Filter
                  </Button>
                  <Button
                    style={{
                      borderRadius: "5px",
                    }}
                    htmlType="button"
                    onClick={() => {
                      const currentMonth = dayjs().format("MMM-YYYY");
                      setDate(currentMonth);
                      getAllProductionPlans({});
                      localStorage.removeItem(
                        "production_planning_filter_value",
                      );
                    }}
                  >
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>

          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              loading={loading}
              error={fetchError}
              onRetry={() => getAllProductionPlans(lastFilterRef.current)}
              emptyText="No records to show yet."
              dataSource={tableData}
              columns={tableColumns}
              sticky={true}
              scroll={{ x: 1200 }}
              pagination
              bordered
            />
          </div>
        </>
      }
    />
  );
};

export default ProductionPlanningList;
